import * as THREE from "three";
import type { MannyBoneDef } from "./mannyRig";

export type RigTable = readonly Readonly<MannyBoneDef>[];
export interface RigProfile {
  readonly fingerprint: string;
  readonly names: ReadonlySet<string>;
  readonly world: readonly THREE.Matrix4[];
  readonly pelvis: THREE.Vector3;
  readonly rootRotation: THREE.Quaternion;
  readonly hipHeight: number;
}
const profiles = new WeakMap<RigTable, RigProfile>();

/** Stable export/runtime identity; quantization matches generated rig tables. */
export function rigFingerprint(rig: RigTable): string {
  const text = JSON.stringify(rig.map(([name, parent, ...rest]) =>
    [name, parent, ...rest.map(n => Math.round(n * 1e6))]));
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  return `humanoid-v1-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export function rigProfile(rig: RigTable): RigProfile {
  const cached = profiles.get(rig);
  if (cached) return cached;
  const names = new Set<string>();
  const world: THREE.Matrix4[] = [];
  for (let i = 0; i < rig.length; i++) {
    const [name, parent, x, y, z, qx, qy, qz, qw] = rig[i];
    if (names.has(name) || parent >= i || parent < -1 || (i > 0 && parent === -1)) throw new Error(`Invalid rig hierarchy at ${name}`);
    if (![x, y, z, qx, qy, qz, qw].every(Number.isFinite)) throw new Error(`Non-finite rest pose at ${name}`);
    names.add(name);
    const local = new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion(qx, qy, qz, qw).normalize(), new THREE.Vector3(1, 1, 1));
    world.push(parent < 0 ? local : new THREE.Matrix4().multiplyMatrices(world[parent], local));
  }
  const pelvisIndex = rig.findIndex(row => row[0] === "pelvis");
  if (pelvisIndex < 0 || rig[pelvisIndex][1] !== 0) throw new Error("Rig requires a pelvis directly beneath root");
  const row = rig[pelvisIndex];
  const rootRotation = new THREE.Quaternion().setFromRotationMatrix(world[0]);
  const hipHeight = new THREE.Vector3().setFromMatrixPosition(world[pelvisIndex]).y;
  if (hipHeight <= 0) throw new Error("Rig must use metres, Y-up, feet at origin");
  const profile = { fingerprint: rigFingerprint(rig), names, world, pelvis: new THREE.Vector3(row[2], row[3], row[4]), rootRotation, hipHeight };
  profiles.set(rig, profile);
  return profile;
}

/** Validate the actual skin, not just exporter metadata. Run once on template ingestion. */
export function validateRigSkin(mesh: THREE.SkinnedMesh, rig: RigTable): void {
  const profile = rigProfile(rig);
  const { bones, boneInverses } = mesh.skeleton;
  if (bones.length > rig.length || bones.length < 89 || boneInverses.length !== bones.length) throw new Error(`${mesh.name}: incompatible joint count`);
  const identity = new THREE.Matrix4();
  for (let i = 0; i < bones.length; i++) {
    if (bones[i].name !== rig[i][0]) throw new Error(`${mesh.name}: joint ${i} is ${bones[i].name}, expected ${rig[i][0]}`);
    const parent = rig[i][1];
    if (parent >= 0 && bones[i].parent !== bones[parent]) throw new Error(`${mesh.name}: wrong parent for ${bones[i].name}`);
    identity.multiplyMatrices(profile.world[i], boneInverses[i]);
    if (identity.elements.some((value, j) => !Number.isFinite(value) || Math.abs(value - (j % 5 === 0 ? 1 : 0)) > 0.0002)) {
      throw new Error(`${mesh.name}: bind pose differs at ${bones[i].name}; rebuild this asset with its rig`);
    }
  }
  const joints = mesh.geometry.getAttribute("skinIndex"), weights = mesh.geometry.getAttribute("skinWeight");
  const count = mesh.geometry.getAttribute("position").count;
  if (!joints || !weights || joints.count !== count || weights.count !== count || joints.itemSize !== 4 || weights.itemSize !== 4) throw new Error(`${mesh.name}: missing or malformed skin attributes`);
  for (let i = 0; i < count; i++) {
    let sum = 0;
    for (let k = 0; k < 4; k++) {
      const joint = joints.getComponent(i, k), weight = weights.getComponent(i, k);
      if (!Number.isInteger(joint) || joint < 0 || joint >= bones.length || !Number.isFinite(weight) || weight < 0) throw new Error(`${mesh.name}: invalid skin influence at vertex ${i}`);
      sum += weight;
    }
    if (Math.abs(sum - 1) > 0.001) throw new Error(`${mesh.name}: unnormalized weights at vertex ${i}`);
  }
}
